import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ForecastV3Provider, useForecastV3, useMonthlyVersions, ForecastUrlSync, useUrlStateForInit } from './context/ForecastV3Context';
import { ForecastHeader, ModuleNav, FORECAST_TABS, RibbonSlotProvider, useRibbonSlotTarget } from './components/layout';
// @ts-ignore - JavaScript module without types
import { usePageCrumb } from '../../context/NavbarActionsContext';
// @ts-ignore - JavaScript module without types
import { useTheme } from '../../context/ThemeContext';
import './forecastV3Inset.css';
import { useAssistantPageContext, buildForecastV3Context } from '../../assistant/context';
import { WorkspacePage } from './components/workspace/WorkspacePage';
import { VersionsPage } from './components/versions/VersionsPage';
import { ComparePage } from './components/compare/ComparePage';
import { AssumptionsPage } from './components/assumptions/AssumptionsPage';
import { HealthPage } from './components/health/HealthPage';
import { NotesPage } from './components/notes';
import { LaborDetailPage } from './components/labor';
import { RiskPage } from './components/risk';
import { AnalyticsPage } from './components/analytics';
import { useVersions } from './hooks/useVersions';
import { useGridDensity } from './hooks/useGridDensity';
import { defaultMonthForYear } from './utils';
// @ts-ignore - JavaScript module without types
import { useUserSettingsContext } from '../../context/UserSettingsContext';
// @ts-ignore - JavaScript module without types
import { getLocationsByOrgId } from '../../utils/locationApi';
// @ts-ignore - JavaScript module without types
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { resolveNativeReportGroups, hasForecastingGrant } from '../../config/reportGroupAccess';
import { useState } from 'react';
import { ArrowLeft, X as CloseIcon } from 'lucide-react';
import { setCompareReturn, useCompareReturn } from './components/compare/compareSession';
import { useNavigate } from 'react-router-dom';

// Create a QueryClient instance for this module
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  },
});

// Helper function for month names
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
function getMonthName(month: number): string {
  return MONTH_NAMES[month - 1] || 'Unknown';
}

// Type for user settings context
interface Organization {
  id: number;
  name: string;
  code?: string;
}

interface UserSettingsContextValue {
  currentOrganization: Organization | null;
  userSettings: unknown;
  isLoading: boolean;
}

interface ForecastModuleV3ContentProps {
  companyName: string;
  propertyName: string;
  orgId: number;
  locations: LocationOption[];
  selectedLocation: LocationOption;
  onLocationChange: (location: LocationOption) => void;
  /** Version ID from URL that should be selected when versions load */
  urlVersionId?: number | null;
  /** Account ID from URL that should be selected when accounts load */
  urlAccountId?: number | null;
  /** Panel tab from URL that should be selected when account is opened */
  urlPanelTab?: string | null;
}

function ForecastModuleV3Content({
  companyName,
  propertyName,
  orgId,
  locations,
  selectedLocation,
  onLocationChange,
  urlVersionId,
  urlAccountId,
  urlPanelTab,
}: ForecastModuleV3ContentProps) {
  const { state, setVersion, setLocationId, setActiveMonth, selectAccount, setPendingAccountId, setPendingDetailPanelTab, setFullscreen, setActiveTab } = useForecastV3();
  const { insetActive } = useTheme();
  const [gridDensity] = useGridDensity();
  const densityAttr = insetActive ? gridDensity : undefined;
  const { activeTab, currentVersion, year, isFullscreen } = state;
  const { activeMonth, monthVersions, updateLockedMonths, getMonthStatus } = useMonthlyVersions();
  const setRibbonSlot = useRibbonSlotTarget();

  // Jumped here from Compare: offer the way back (restoring the version the
  // jump may have switched). Wandering to any other tab drops the offer.
  const compareReturn = useCompareReturn();
  useEffect(() => {
    if (compareReturn && activeTab !== 'workspace' && activeTab !== 'compare') setCompareReturn(null);
  }, [compareReturn, activeTab]);
  const backToCompare = () => {
    if (!compareReturn) return;
    const original = versions.find((v) => v.id === compareReturn.versionId);
    if (original && original.id !== currentVersion?.id) setVersion(original);
    setActiveTab('compare');
  };
  // Inset shell: the module's sections ride the shell breadcrumb as the page
  // switcher (Planning › Budget & Forecasting › Workspace ⌃), which frees the
  // ribbon for working controls. Classic keeps the tab strip under the ribbon.
  const activeTabTitle = FORECAST_TABS.find((t) => t.id === activeTab)?.label ?? 'Workspace';
  usePageCrumb(insetActive && !isFullscreen ? {
    title: activeTabTitle,
    options: FORECAST_TABS.map((t) => ({ key: t.id, title: t.label })),
    onSelect: (key: string) => setActiveTab(key as typeof activeTab),
    back: compareReturn && activeTab === 'workspace'
      ? { label: 'Back to Compare', title: `You opened ${compareReturn.accountLabel} from Compare`, onClick: backToCompare, onDismiss: () => setCompareReturn(null) }
      : null,
  } : null);
  // The breadcrumb carries the chip in the inset shell; elsewhere it floats.
  const crumbCarriesBack = insetActive && !isFullscreen;

  // Forecast actions open the one app-level assistant. Keeping a second
  // Forecast AI component here used to split history and killed in-flight work
  // when this module navigated or unmounted.
  //
  // Every entry point RUNS something when clicked — it opens the assistant
  // with an action to execute, not a sentence sitting in the input box
  // waiting for Enter.
  const runAssistantAction = useCallback((action: {
    prompt: string;
    promptKey?: string;
    workflowRef?: string;
    workflowContext?: { versionId: number | null; year: number | null };
    label?: string;
  }) => {
    window.dispatchEvent(new CustomEvent('lodgeic:assistant:open', {
      detail: {
        expanded: false,
        hotelId: String(selectedLocation.id),
        hotelName: selectedLocation.name,
        action,
      }
    }));
  }, [selectedLocation.id, selectedLocation.name]);

  // "Analyze with AI" (workspace toolbar) runs the accuracy-review WORKFLOW —
  // the same versioned ref the AI panel's "Analyze & improve accuracy" action
  // launches. A button that sends a sentence and hopes the model picks the
  // right tools is not the same product as one that runs a named workflow.
  const openAiAnalyze = useCallback(() => {
    const isBudgetVersion = currentVersion?.type === 'Budget';
    const kind = isBudgetVersion ? 'budget' : 'forecast';
    runAssistantAction({
      prompt: isBudgetVersion
        ? 'Analyze this budget against recent actuals and last year. Show me where the numbers look aggressive or light, explain why, and propose updated budget amounts.'
        : "Analyze this forecast's accuracy. Show me where the biggest variances are, explain why, and propose updated forecast amounts.",
      promptKey: 'forecast_analyze_improve_accuracy',
      // Budgets run the governed build workflow. Forecast accuracy runs the
      // canned AGENT flow (analyze the month → inspect drivers → a
      // forecast_changes card of explained, concrete changes) — the 08-18
      // switch to forecast_accuracy_review.v1 replaced that with a narrow
      // deterministic pass that mostly said "no change" (Derek, 2026-08-20).
      ...(isBudgetVersion ? { workflowRef: 'revenue_budget.v2' } : {}),
      workflowContext: { versionId: currentVersion?.id ?? null, year: year ?? null },
      label: `Analyze ${kind} accuracy`,
    });
  }, [currentVersion?.type, currentVersion?.id, year, runAssistantAction]);

  // "Build drivers with AI": the deterministic full-coverage builder — every
  // driverless account, plus every account whose driver is the wrong method.
  // A FORECAST runs the governed forecast_build workflow, which plans Rooms
  // at stat-account grain (room nights × ADR per statistic account) exactly
  // like the budget build and hands every other account to the driver
  // engine. A budget keeps the plain prompt: the router's deterministic
  // budget-build rule sends it to revenue_budget.v2.
  const openAiBuildDrivers = useCallback(() => {
    const isBudgetVersion = currentVersion?.type === 'Budget';
    const kind = isBudgetVersion ? 'budget' : 'forecast';
    runAssistantAction({
      prompt: `Build this ${kind} (version ${currentVersion?.id}, ${year}): set the right driver on every account that has none, and reassign any account whose current driver is the wrong method for it.`,
      ...(isBudgetVersion ? {} : {
        workflowRef: 'forecast_build.v1',
        workflowContext: { versionId: currentVersion?.id ?? null, year: year ?? null },
      }),
      label: `Build ${kind} drivers`,
    });
  }, [currentVersion?.type, currentVersion?.id, year, runAssistantAction]);

  // "Compare to on-the-books & RMS": the external accuracy check.
  const openAiCompareOtb = useCallback(() => {
    runAssistantAction({
      prompt: `Compare this forecast's rooms revenue to what is on the books and to the RMS forecast for version ${currentVersion?.id}, ${year}. Is it achievable, and which months look wrong?`,
      label: 'Forecast vs on-the-books',
    });
  }, [currentVersion?.id, year, runAssistantAction]);

  // "Review budget": the read-only completed-budget audit (budget_review.v1).
  // Offered on any budget version status; notes/checklist attached in the
  // chat are read by the workflow.
  const openAiReviewBudget = useCallback(() => {
    runAssistantAction({
      prompt: 'Review this completed budget against trailing history, last year, known anomalies and any notes or checklist I attached, and flag what looks wrong before we finalize it.',
      workflowRef: 'budget_review.v1',
      workflowContext: { versionId: currentVersion?.id ?? null, year: year ?? null },
      label: 'Review budget',
    });
  }, [currentVersion?.id, year, runAssistantAction]);

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, setFullscreen]);

  // Track if URL-specified version has been applied
  const urlVersionAppliedRef = useRef(false);
  // Track if URL-specified account has been set to pending
  const urlAccountAppliedRef = useRef(false);
  // Track if URL-specified panel tab has been set to pending
  const urlPanelTabAppliedRef = useRef(false);

  // Fetch versions from real API
  const {
    versions,
    defaultVersionId,
    setDefaultVersion,
    lockMonth,
    unlockMonth,
    isLoading: isLoadingVersions,
  } = useVersions({
    orgId,
    locationId: selectedLocation.id,
    year,
    enabled: !!orgId && !!selectedLocation.id,
  });

  // Set initial version when versions load, or re-select when the location or
  // selected year no longer matches the current version (e.g. header year nav)
  useEffect(() => {
    if (versions.length > 0) {
      if (!currentVersion || currentVersion.locationId !== selectedLocation.id || currentVersion.year !== year) {
        // Priority 1: URL-specified version (only apply once)
        if (urlVersionId && !urlVersionAppliedRef.current) {
          const urlVersion = versions.find(v => v.id === urlVersionId);
          if (urlVersion) {
            setVersion(urlVersion);
            urlVersionAppliedRef.current = true;
            return; // Don't set active month here - URL state should handle it
          }
        }

        // Priority 2: Default version if one is set
        const defaultVersion = defaultVersionId
          ? versions.find(v => v.id === defaultVersionId)
          : null;
        // Priority 3: First forecast version
        const forecastVersion = versions.find(v => v.versionType !== 'budget');
        setVersion(defaultVersion || forecastVersion || versions[0]);

        // Default the working month for the selected year (skip only while the
        // initial URL month is still pending)
        if (!urlVersionId || urlVersionAppliedRef.current) {
          setActiveMonth(defaultMonthForYear(year));
        }
      }
    } else if (!isLoadingVersions) {
      // No versions available for this location, clear current version
      setVersion(null);
    }
  }, [versions, currentVersion, selectedLocation.id, year, isLoadingVersions, setVersion, defaultVersionId, setActiveMonth, urlVersionId]);

  // Sync location changes with context (for data refetch)
  useEffect(() => {
    if (selectedLocation?.id) {
      setLocationId(selectedLocation.id);
    }
  }, [selectedLocation?.id, setLocationId]);

  // Set pending account ID and panel tab from URL (WorkspacePage will handle the actual selection)
  useEffect(() => {
    if (urlAccountId && !urlAccountAppliedRef.current) {
      setPendingAccountId(urlAccountId);
      urlAccountAppliedRef.current = true;
    }
  }, [urlAccountId, setPendingAccountId]);

  // Set pending panel tab from URL (to be applied after account is selected)
  useEffect(() => {
    if (urlPanelTab && !urlPanelTabAppliedRef.current) {
      const validTabs = ['driver', 'adjustments', 'overrides', 'notes'];
      if (validTabs.includes(urlPanelTab)) {
        setPendingDetailPanelTab(urlPanelTab as 'driver' | 'adjustments' | 'overrides' | 'notes');
        urlPanelTabAppliedRef.current = true;
      }
    }
  }, [urlPanelTab, setPendingDetailPanelTab]);

  // Handle month selection from header
  const handleMonthSelect = useCallback((month: number) => {
    setActiveMonth(month);
  }, [setActiveMonth]);

  // Handle locking the active month
  const handleLockMonth = useCallback(async () => {
    if (!currentVersion || activeMonth === null) return;

    const status = getMonthStatus(activeMonth);
    if (status === 'locked') {
      toast.error(`${getMonthName(activeMonth)} is already locked`);
      return;
    }

    try {
      const lockedMonths = await lockMonth(String(currentVersion.id), activeMonth);
      updateLockedMonths(lockedMonths);
      toast.success(`Locked ${getMonthName(activeMonth)} ${year}`);

      // Move to the next unlocked month
      const nextMonth = activeMonth < 12 ? activeMonth + 1 : null;
      if (nextMonth) {
        setActiveMonth(nextMonth);
      }
    } catch (error) {
      console.error('Failed to lock month:', error);
      toast.error('Failed to lock month');
    }
  }, [currentVersion, activeMonth, year, lockMonth, updateLockedMonths, setActiveMonth, getMonthStatus]);

  // Handle unlocking a month
  const handleUnlockMonth = useCallback(async (month: number) => {
    if (!currentVersion) return;

    try {
      const lockedMonths = await unlockMonth(String(currentVersion.id), month);
      updateLockedMonths(lockedMonths);
      toast.success(`Unlocked ${getMonthName(month)} ${year}`);
    } catch (error) {
      console.error('Failed to unlock month:', error);
      toast.error('Failed to unlock month');
    }
  }, [currentVersion, year, unlockMonth, updateLockedMonths]);

  // Render active tab content
  const backPill = compareReturn && activeTab === 'workspace' && !crumbCarriesBack ? (
    <div
      className="fixed bottom-[68px] left-1/2 -translate-x-1/2 z-[200] flex items-center gap-1 pl-1 pr-1 h-[38px] rounded-full"
      style={{ background: 'var(--color-success-bg)', color: 'var(--color-success-ink)', border: '1px solid color-mix(in srgb, var(--color-success-ink) 28%, transparent)', boxShadow: 'var(--shadow-lg)' }}
    >
      <button type="button" onClick={backToCompare} className="flex items-center gap-2 h-[30px] pl-3 pr-3 rounded-full text-[12.5px] font-semibold hover:bg-black/5" title={`You opened ${compareReturn.accountLabel} from Compare`}>
        <ArrowLeft size={14} />
        Back to Compare
      </button>
      <button type="button" onClick={() => setCompareReturn(null)} className="w-[26px] h-[26px] rounded-full flex items-center justify-center hover:bg-black/5" aria-label="Dismiss">
        <CloseIcon size={13} />
      </button>
    </div>
  ) : null;

  const renderContent = () => {
    switch (activeTab) {
      case 'workspace':
        return <WorkspacePage
            locationCurrency={selectedLocation.currency || 'USD'}
            locationName={selectedLocation.name}
            onAnalyzeWithAi={openAiAnalyze}
            onBuildDriversWithAi={openAiBuildDrivers}
            onCompareToOtbWithAi={openAiCompareOtb}
            onReviewBudgetWithAi={currentVersion?.type === 'Budget' ? openAiReviewBudget : undefined}
          />;
      case 'labor':
        return <LaborDetailPage />;
      case 'versions':
        return <VersionsPage versions={versions} />;
      case 'compare':
        return <ComparePage versions={versions} currency={selectedLocation.currency || 'USD'} />;
      case 'assumptions':
        return <AssumptionsPage />;
      case 'health':
        return <HealthPage />;
      case 'risk':
        return <RiskPage />;
      case 'analytics':
        return <AnalyticsPage currency={selectedLocation.currency || 'USD'} />;
      case 'notes':
        return <NotesPage />;
      default:
        return <WorkspacePage
            locationCurrency={selectedLocation.currency || 'USD'}
            onAnalyzeWithAi={openAiAnalyze}
            onBuildDriversWithAi={openAiBuildDrivers}
            onCompareToOtbWithAi={openAiCompareOtb}
            onReviewBudgetWithAi={currentVersion?.type === 'Budget' ? openAiReviewBudget : undefined}
          />;
    }
  };

  // Height fills the app shell's inset card below its sticky header
  // (viewport − top gap − header − bottom gap − card border). The route is
  // full-bleed in MainLayout; the module scrolls internally.

  // ── Assistant page context ──────────────────────────────────────────────
  // Published by the PAGE, not by the chat popup. The assistant is docked to
  // the app frame now, so a context that only existed while the popup was open
  // meant the ambient launcher saw nothing on the one page that has the richest
  // context to give it.
  const assistantOrgId = useMemo(() => {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('currentOrgId') : null;
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isInteger(parsed) ? parsed : null;
  }, []);

  const assistantPageState = useMemo(() => (
    selectedLocation
      ? buildForecastV3Context({
        orgId: assistantOrgId,
        locationId: selectedLocation.id,
        locationName: selectedLocation.name,
        versionId: state.currentVersion?.id ?? null,
        versionName: state.currentVersion?.name ?? null,
        versionType: state.currentVersion?.versionType ?? 'forecast',
        year: state.year ?? null,
        activeMonth: state.activeMonth ?? null,
        // Selecting a row adds it to the assistant's context, so "why is this
        // wrong?" needs no restatement. The id is a HINT: the server rehydrates
        // the account against the authorized property and drops it if it
        // cannot. Only the reference travels — never the row's values.
        selection: state.selectedAccount
          ? {
            kind: 'forecast_account' as const,
            id: state.selectedAccount.chartOfAccountId,
            label: state.selectedAccount.accountName,
          }
          : null,
      })
      : null
  ), [
    assistantOrgId, selectedLocation, state.currentVersion?.id, state.currentVersion?.name,
    state.currentVersion?.versionType, state.year, state.activeMonth,
    state.selectedAccount?.chartOfAccountId, state.selectedAccount?.accountName,
  ]);

  useAssistantPageContext(assistantPageState);

  // Fullscreen mode - use portal to render at document body level to escape parent stacking contexts
  if (isFullscreen) {
    return createPortal(
      <div className="fv3-root fv3-fullscreen fixed inset-0 z-[9999] flex" data-fv3-density={densityAttr} style={{ background: 'var(--lodge-white)' }}>
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="fv3-ribbon flex items-center flex-none min-w-0" style={{ background: 'var(--lodge-white)', borderBottom: '1px solid var(--lodge-onyx-100)' }}>
            <ForecastHeader
              compact
              companyName={companyName}
              propertyName={propertyName}
              versions={versions}
              monthVersions={monthVersions}
              defaultVersionId={defaultVersionId}
              locations={locations}
              selectedLocation={selectedLocation}
              onLocationChange={onLocationChange}
              onSetDefaultVersion={setDefaultVersion}
              onMonthSelect={handleMonthSelect}
              onLockMonth={handleLockMonth}
              onUnlockMonth={handleUnlockMonth}
              reserveTrayGap={false}
            />
            <div ref={setRibbonSlot} className="fv3-ribbon-slot flex-1 min-w-0 flex items-center justify-end" />
          </div>
          <ModuleNav />
          <div className="fv3-content flex-1 flex overflow-hidden" data-fv3-tab={activeTab} style={{ background: 'var(--lodge-onyx-50)' }}>
            {renderContent()}
            {backPill}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Normal mode
  return (
    <div
      className="fv3-root flex overflow-hidden"
      data-fv3-density={densityAttr}
      style={insetActive
        // Inset shell: fill the card below its sticky header.
        ? { height: 'calc(100svh - var(--shell-sticky-top, 0px) - var(--shell-gap, 0px) - 2px)' }
        // Classic shell: match the sidebar rail (inset 16px top/bottom) under MainLayout's pt-6.
        : { height: 'calc(100vh - 32px)', marginTop: -8 }}
    >
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* One ribbon: property · period · version on the left, the active
            page's working controls on the right (portaled in). The classic
            shell keeps the tab strip beneath it; the inset shell has moved the
            tabs into the breadcrumb, so the strip is hidden there. */}
        <div
          className="fv3-ribbon flex items-center flex-none min-w-0 rounded-t-xl"
          style={{ background: 'var(--lodge-white)', borderBottom: '1px solid var(--lodge-onyx-100)', paddingRight: insetActive ? 0 : 148 }}
        >
          <ForecastHeader
            compact
            hoistPickers={insetActive}
            companyName={companyName}
            propertyName={propertyName}
            versions={versions}
            monthVersions={monthVersions}
            defaultVersionId={defaultVersionId}
            locations={locations}
            selectedLocation={selectedLocation}
            onLocationChange={onLocationChange}
            onSetDefaultVersion={setDefaultVersion}
            onMonthSelect={handleMonthSelect}
            onLockMonth={handleLockMonth}
            onUnlockMonth={handleUnlockMonth}
            reserveTrayGap={false}
          />
          <div ref={setRibbonSlot} className="fv3-ribbon-slot flex-1 min-w-0 flex items-center justify-end" />
        </div>
        {!insetActive && <ModuleNav />}
        <div className="fv3-content flex-1 flex overflow-hidden" data-fv3-tab={activeTab} style={{ background: 'var(--lodge-onyx-50)' }}>
          {renderContent()}
          {backPill}
        </div>
      </div>
    </div>
  );
}

interface ForecastModuleV3Props {
  initialVersionId?: number;
  dateRange?: { start: string; end: string };
}

interface LocationOption {
  id: number;
  name: string;
  orgId: number;
  currency?: string | null;
}

export function ForecastModuleV3({
  initialVersionId,
  dateRange,
}: ForecastModuleV3Props) {
  const { currentOrganization, userSettings, isLoading: isLoadingUserSettings } = useUserSettingsContext() as UserSettingsContextValue;
  const forecastV3Enabled = useFeatureFlagEnabled('forecasting');
  // Explicit per-user grant (User setup → Report Access) bypasses the flag.
  const forecastingGranted = hasForecastingGrant(resolveNativeReportGroups(currentOrganization, userSettings));
  const navigate = useNavigate();
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  // Get URL state for initialization
  const { urlState, hasUrlParams } = useUrlStateForInit();

  // Redirect if the feature flag is off AND no admin grant covers this user
  useEffect(() => {
    if (forecastV3Enabled === false && !forecastingGranted) {
      navigate('/dashboard');
    }
  }, [forecastV3Enabled, forecastingGranted, navigate]);

  // Fetch locations when org changes
  useEffect(() => {
    if (!currentOrganization?.id) {
      setLocations([]);
      setSelectedLocation(null);
      return;
    }

    const fetchLocations = async () => {
      setIsLoadingLocations(true);
      try {
        const locationsData = await getLocationsByOrgId(currentOrganization.id);
        if (Array.isArray(locationsData) && locationsData.length > 0) {
          setLocations(locationsData);
          // Auto-select location if none selected
          if (!selectedLocation || selectedLocation.orgId !== currentOrganization.id) {
            // Priority 1: URL-specified location
            if (hasUrlParams && urlState.locationId) {
              const urlLocation = locationsData.find((loc: LocationOption) => loc.id === urlState.locationId);
              if (urlLocation) {
                setSelectedLocation(urlLocation);
              } else {
                // URL location not found, fall back to first
                setSelectedLocation(locationsData[0]);
              }
            } else {
              // Priority 2: First location
              setSelectedLocation(locationsData[0]);
            }
          }
        } else {
          setLocations([]);
          setSelectedLocation(null);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
        setLocations([]);
        setSelectedLocation(null);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [currentOrganization?.id, hasUrlParams, urlState.locationId]);

  const initialDateRange = dateRange || {
    start: '2026-01-01',
    end: '2026-12-31',
  };

  const currentYear = new Date().getFullYear();

  if (forecastV3Enabled === null || forecastV3Enabled === false) {
    return <div className="flex-1 flex items-center justify-center"><div className="text-slate-500">Checking feature availability...</div></div>;
  }

  // Show loading state while fetching user settings or locations
  if (isLoadingUserSettings || isLoadingLocations) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  // Show message if no organization selected
  if (!currentOrganization?.id) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-slate-500">Please select an organization</div>
      </div>
    );
  }

  // Show message if no location available
  if (!selectedLocation) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-slate-500">No locations available for this organization</div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ForecastV3Provider
        initialOrgId={currentOrganization.id}
        initialLocationId={selectedLocation.id}
        initialYear={hasUrlParams && urlState.year ? urlState.year : currentYear}
        initialVersion={undefined}
        initialDateRange={initialDateRange}
      >
        <ForecastUrlSync>
          <RibbonSlotProvider>
          <ForecastModuleV3Content
            companyName={currentOrganization.name || 'Organization'}
            propertyName={selectedLocation.name}
            orgId={currentOrganization.id}
            locations={locations}
            selectedLocation={selectedLocation}
            onLocationChange={setSelectedLocation}
            urlVersionId={hasUrlParams ? urlState.versionId : null}
            urlAccountId={hasUrlParams ? urlState.selectedAccountId : null}
            urlPanelTab={hasUrlParams ? urlState.detailPanelTab : null}
          />
          </RibbonSlotProvider>
        </ForecastUrlSync>
      </ForecastV3Provider>
    </QueryClientProvider>
  );
}

export default ForecastModuleV3;
